import React from "react";

export default function TaskCompletionTimeChart(): JSX.Element {
  // Set dimensions and margins
  const width = 1110;
  const height = 700;
  const margin = { top: 60, right: 30, bottom: 120, left: 140 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  // Define the y-axis scale (log scale for time)
  const timeValues = [
    "4 sec", "8 sec", "15 sec", "30 sec",
    "1 min", "2 min", "4 min", "8 min", "15 min", "30 min",
    "1 hr", "2 hrs", "4 hrs", "8 hrs", "16 hrs", "1 week", "2 weeks", 
    "1 month", "2 months", "4 months", "8 months", "16 months", "32 months", "5 years"
  ];
  
  const gptcolor = "#003000";
  const claudecolor = "#832000";
  const forecastcolor = "#40c040";
  const newcolor = "#4EC2F9";
  const newcolor2 = "#215F9A";
  // Define years for x-axis
  const years = ["2021", "2022", "2023", "2024", "2025", "2026", "2027", "2028"];
  
  // Model data points - including some from the original chart
  const modelData = [
    { model: "gpt-3.5-turbo-instruct", x: 2022.2, y: 1.45, color: gptcolor, shape: "triangle" },
    { model: "GPT-4 0314", x: 2023.23, y: 4, color: gptcolor, shape: "square" },
    { model: "GPT-4 1106", x: 2023.88, y: 4.55, color: gptcolor, shape: "diamond" },
    { model: "GPT-4o", x: 2024.3, y: 4.8, color: gptcolor, shape: "diamond2" },
    { model: "o1-preview", x: 2024.65, y: 6.2, color: gptcolor, shape: "cross" },
    { model: "o1", x: 2024.9, y: 6.7, color: gptcolor, shape: "plus" },
    { model: "Claude 3.5 Sonnet (Old)", x: 2024.45, y: 5.65, color: claudecolor, shape: "triangle" },
    { model: "Claude 3.5 Sonnet (New)", x: 2024.75, y: 6.2, color: claudecolor, shape: "square" },
    { model: "Claude 3.7 Sonnet", x: 2025.15, y: 8, color: claudecolor, shape: "diamond" },
    { model: "o4-mini", x: 2025.3, y: 8, color: newcolor2, shape: "plus" },
    { model: "o3", x: 2025.3, y: 8.5, color: newcolor, shape: "cross" },
    { model: "Agent-0", x: 2025.6, y: 10, color: forecastcolor, shape: "circle" },
    { model: "Agent-1", x: 2026.5, y: 16.7, color: forecastcolor, shape: "triangle" },
    { model: "Agent-2", x: 2027, y: 22.2, color: forecastcolor, shape: "square" }
  ];
  
    // Calculate scaling functions
    const xScale = (year: number): number => {
      const yearRange = [2021, 2028];
      return margin.left + ((year - yearRange[0]) / (yearRange[1] - yearRange[0])) * innerWidth;
    };
    
    const yScale = (index: number): number => {
      return margin.top + innerHeight - (index / (timeValues.length - 1)) * innerHeight;
    };
    // Superexponential parameters (tunable)
    let baseYear = 2021;   // Starting year
    let startValue = 1.45;
    let initialDoublingTime = 1.91;
    let doublingFactorQ = 0.85;
    let maxYValue = 24;
    // const a = 2;             // Vertical shift: starting y value
    // const b = 1;             // Scale factor (initial multiplier)
    // const r = .395;           // Base growth rate (controls overall speed)
    // const p = 1.15;           // Curvature parameter: p > 1 makes each doubling on the y-axis easier
    
    const trendlineFunction = (year: number): number => {
      if (year < baseYear) return NaN; // Do not plot before baseYear
  
      let t = year - baseYear; // Time elapsed since baseYear
  
      if (t === 0) return startValue; // Value at the start year
  
      let q = doublingFactorQ;
      let T1 = initialDoublingTime;
      let y0 = startValue;
  
      // Prevent division by zero or log of non-positive number
      if (T1 <= 0 || q <= 0 || q === 1.0) {
          console.error("Invalid trendline parameters: T1 > 0, 0 < q < 1 required.");
          return NaN;
      }
  
      const termInsideLog = 1 - (t * (1 - q)) / T1;
  
      // Check if we are approaching or past the vertical asymptote
      // The asymptote occurs when termInsideLog becomes 0 or negative.
      // t_asymptote = T1 / (1 - q)
      if (termInsideLog <= 0) {
          // The function goes to infinity at or before this time 't'
          return NaN; // Return NaN to avoid plotting beyond the asymptote
                      // or return a very large number if appropriate for your scale
      }
  
      // Calculate n using the formula derived above
      const n = Math.log(termInsideLog) / Math.log(q);
  
      // Calculate the y-value: y = y0 * 2^n
      const y = y0 * Math.pow(2, n);
  
      // Apply the upper limit if desired
      return (y > maxYValue) ? NaN : y;
    };
    
    // Generate trendline path data
    const generateTrendline = (): string => {
      const steps = 130; // Number of points to calculate for the line smoothness
      const plotEndYear = 2028; // The end year for plotting the trendline
      const yearRange = [baseYear, plotEndYear];
      const yearSpan = yearRange[1] - yearRange[0];
  
      if (yearSpan <= 0) return ""; // Cannot generate path for zero span
  
      let pathData = "";
      let firstPoint = true;
  
      for (let i = 0; i <= steps; i++) {
        const year = yearRange[0] + (i / steps) * yearSpan;
        const yValue = trendlineFunction(year); // Use the calculated y-value directly
  
        // Skip points that returned NaN (before baseYear, past asymptote, or past maxYValue)
        if (isNaN(yValue)) continue;
  
        const x = xScale(year);
        const y = yScale(yValue); // Use yValue directly with yScale
  
        // Ensure x and y are valid numbers before adding to path
        if (isNaN(x) || isNaN(y)) continue;
  
        if (firstPoint) {
          pathData = `M ${x} ${y}`;
          firstPoint = false;
        } else {
          pathData += ` L ${x} ${y}`;
        }
      }
  
      return pathData;
    };
  
    function splitTrendline(pathStr: string): [string, string] {
      const commands = pathStr.match(/[ML][^ML]+/g);
    
      if (!commands || commands.length < 2) {
        return [pathStr, ''];
      }
    
      const midIndex = Math.floor(commands.length / 1.45);
      const firstHalf = commands.slice(0, midIndex + 1).join('');
      const secondHalf = 'M' + commands[midIndex].slice(1) + commands.slice(midIndex + 1).join('');
    
      return [firstHalf, secondHalf];
    }  
    
    const [firstHalfPath, secondHalfPath] = splitTrendline(generateTrendline());
    // Generate METR's trendline path as a linear line 
    const generateMetrTrendline1 = (): string => {
      const metrStartYear = 2021;
      const metrEndYear = 2025.125;
      const metrStartY = 0;
      const metrEndY = 7.05;
      
      const metrPathData = `M ${xScale(metrStartYear)} ${yScale(metrStartY)} L ${xScale(metrEndYear)} ${yScale(metrEndY)}`;
      
      return metrPathData;
    };
    const generateMetrTrendline2 = (): string => {
      const metrStartYear = 2025.125;
      const metrEndYear = 2028;
      const metrStartY = 7.05;
      const metrEndY = 12;
      
      const metrPathData = `M ${xScale(metrStartYear)} ${yScale(metrStartY)} L ${xScale(metrEndYear)} ${yScale(metrEndY)}`;
      
      return metrPathData;
    };
    // split after 2025
    const firstHalfMETRPath = generateMetrTrendline1();
    const secondHalfMETRPath = generateMetrTrendline2();
  
    // Draw shapes based on model type
    const drawModelPoint = (model: any) => {
      const x = xScale(model.x);
      const y = yScale(model.y);
      const size = 15;
      
      if (model.shape === "triangle") {
        return (
          <polygon
            points={`${x},${y-size/1.1} ${x-size/1.1},${y+size/1.1} ${x+size/1.1},${y+size/1.1}`}
            fill={model.color}
            stroke="white"
            strokeWidth={2}
          />
        );
      } else if (model.shape === "square") {
        return (
          <rect
            x={x - size/1.5}
            y={y - size/1.5}
            width={size*1.4}
            height={size*1.4}
            fill={model.color}
            stroke="white"
            strokeWidth={2}
          />
        );
      } else if (model.shape === "diamond") {
        return (
          <polygon
            points={`${x},${y-size} ${x+size},${y} ${x},${y+size} ${x-size},${y}`}
            fill={model.color}
            stroke="white"
            strokeWidth={2}
          />
        );
      } else if (model.shape === "diamond2") {
          return (
            <polygon
              points={`${x},${y-size} ${x+size/1.5},${y} ${x},${y+size} ${x-size/1.5},${y}`}
              fill={model.color}
              stroke="white"
              strokeWidth={2}
            />
          );
        } else if (model.shape === "plus") {
          return (
            <g>
              <line
                x1={x - size/1.1}
                y1={y}
                x2={x + size/1.1}
                y2={y}
                stroke={model.color}
                strokeWidth={7}
              />
              <line
                x1={x}
                y1={y - size/1.1}
                x2={x}
                y2={y + size/1.1}
                stroke={model.color}
                strokeWidth={7}
              />
            </g>
          );
        } else if (model.shape === "cross") {
          return (
            <g>
              <line
                x1={x - size/1.6}
                y1={y - size/1.6}
                x2={x + size/1.6}
                y2={y + size/1.6}
                stroke={model.color}
                strokeWidth={7}
              />
              <line
                x1={x - size/1.6}
                y1={y + size/1.6}
                x2={x + size/1.6}
                y2={y - size/1.6}
                stroke={model.color}
                strokeWidth={7}
              />
            </g>
          );
        } else {
        return (
          <circle
            cx={x}
            cy={y}
            r={size/1.2}
            fill={model.color}
            stroke="white"
            strokeWidth={2}
          />
        );
      }
    };
  
    return (
      <div className="w-full relative overflow-hidden mt-8 mb-8">
        <svg
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          viewBox={`0 0 ${width} ${height}`}
        >
          {/* Title */}
          <text
            x={xScale(2020.7)}
            y={margin.top - 35}
            textAnchor="left"
            fontSize="28"
            fontFamily="monospace"
            // fontWeight="bold"
            fill="#333333"
            className="text-[28px] font-mono"
          >
            Length Of Coding Tasks AI Agents Can Complete Autonomously
          </text>
          <text
            x={width / 2}
            y={margin.top / 2 +20}
            textAnchor="middle"
            fontSize="14"
            fontFamily="monospace"
            fill="#333333"
            className="text-[14px] font-mono"
          >
            80% success rate
          </text>
          
          {/* Y-axis label */}
          <text
            transform={`rotate(-90, ${margin.left / 3}, ${margin.top + innerHeight / 2})`}
            x={margin.left / 3}
            y={margin.top + innerHeight / 2.2}
            textAnchor="middle"
            fontSize="20"
            fontFamily="monospace"
            fill="#333333"
            className="text-[20px] font-mono"
          >
            Task time (for humans), 80% success rate
          </text>
          
          {/* X-axis label */}
          <text
            x={margin.left + innerWidth / 2}
            y={margin.top + innerHeight + 55}
            textAnchor="middle"
            fontSize="22"
            fontFamily="monospace"
            fill="#333333"
            className="text-[22px] font-mono"
          >
            Model release date
          </text>
          
          {/* Grid lines - horizontal */}
          {timeValues.map((_, i) => (
            <line
              key={`hgrid-${i}`}
              x1={margin.left}
              y1={yScale(i)}
              x2={margin.left + innerWidth}
              y2={yScale(i)}
              stroke="#CCCCCC"
              strokeWidth="1"
              opacity="0.5"
            />
          ))}
          
          {/* Grid lines - vertical */}
          {years.map((year) => (
            <line
              key={`vgrid-${year}`}
              x1={xScale(parseInt(year))}
              y1={margin.top}
              x2={xScale(parseInt(year))}
              y2={margin.top + innerHeight}
              stroke="#CCCCCC"
              strokeWidth="1"
              opacity="0.5"
            />
          ))}
          
          {/* Y-axis ticks and labels - reduced frequency */}
          {timeValues.map((time, i) => (
            <g key={`ytick-${i}`}>
              <line
                x1={margin.left - 6+3*(1-i % 2)}
                y1={yScale(i)}
                x2={margin.left}
                y2={yScale(i)}
                stroke="#333333"
                strokeWidth="1.5"
              />
              <text
                x={margin.left - 10}
                y={(i % 2 === 1) ? yScale(i) + 5: -10}
                textAnchor="end"
                fontSize="18"
                fontFamily="monospace"
                fill="#333333"
                className="text-[18px] font-mono"
              >
                {time}
              </text>
            </g>
          ))}
          
          {/* X-axis ticks and labels */}
          {years.map((year) => (
            <g key={`xtick-${year}`}>
              <line
                x1={xScale(parseInt(year))}
                y1={margin.top + innerHeight}
                x2={xScale(parseInt(year))}
                y2={margin.top + innerHeight + 5}
                stroke="#333333"
                strokeWidth="1.5"
              />
              <text
                x={xScale(parseInt(year))}
                y={margin.top + innerHeight + 25}
                textAnchor="middle"
                fontSize="18"
                fontFamily="monospace"
                fill="#333333"
                className="text-[18px] font-mono"
              >
                {year}
              </text>
            </g>
          ))}
          
          {/* Superexponential trendline */}
          <path
          d={firstHalfPath}
          fill="none"
          stroke="#104010"
          strokeWidth="3"
          opacity="0.8"
          />
          <path
          d={secondHalfPath}
          fill="none"
          stroke="#209020"
          strokeWidth="3"
          strokeDasharray="7,7"
          opacity="0.8"
          />
  
          <path
          d={firstHalfMETRPath}
          fill="none"
          // stroke dark blue
          stroke="#101090"
          strokeWidth="3"
          opacity="0.8"
          />
          <path
          d={secondHalfMETRPath}
          fill="none"
          stroke="#4040c0"
          strokeWidth="3"
          strokeDasharray="7,7"
          opacity="0.8"
          />
          {/* Trendline info box - larger with title but no sliders */}
          <g transform={`translate(${margin.left + innerWidth - 320}, ${margin.top +330})`}>
            <rect x="0" y="0" width="310" height="180" fill="#F5F5F5" rx="5" opacity="0.9" />
            
            <text 
              x="100" 
              y="30" 
              fontSize="18" 
              fontWeight="bold" 
              fontFamily="monospace"
              className="text-[18px] font-mono"
            >
              TRENDLINES
            </text>
            <text 
              x="15" 
              y="60" 
              fontSize="18" 
              fontWeight="bold" 
              fontFamily="monospace"
              className="text-[18px] font-mono"
            >
              Exponential
            </text>
            <line
            x1={205}
            y1={53}
            x2={245}
            y2={53}
            stroke="#101090"
            strokeWidth="3"
            />
            <line
            x1={245}
            y1={53}
            x2={295}
            y2={53}
            // dashed line
            strokeDasharray="7,7"
            stroke="#4040c0"
            strokeWidth="3"
            />
            <text 
              x="15" 
              y="90" 
              fontSize="14" 
              fontFamily="monospace"
              className="text-[16px] font-mono"
              fill="#333333"
            >
              METR's 7 month doubling time
  
            </text>
            <text 
              x="15" 
              y="130" 
              fontSize="18" 
              fontWeight="bold" 
              fontFamily="monospace"
              className="text-[18px] font-mono"
            >
              Superexponential
            </text>
            <line
            x1={205}
            y1={123}
            x2={245}
            y2={123}
            stroke="#104010"
            strokeWidth="3"
            />
            <line
            x1={245}
            y1={123}
            x2={295}
            y2={123}
            // dashed line
            strokeDasharray="7,7"
            stroke="#209020"
            strokeWidth="3"
            />
            <text 
              x="15" 
              y="160" 
              fontSize="14" 
              fontFamily="monospace"
              className="text-[16px] font-mono"
              fill="#333333"
            >
              Each doubling gets 15% easier
              <tspan dy="-4">
                  *
              </tspan>
            </text>
          </g>
          <text 
              x="0" 
              y="668" 
              fontSize="15" 
              fontFamily="monospace"
              className="text-[15px] font-mono"
              fill="#333333"
            >
              <tspan dy="-5">*</tspan>
              <tspan dy="+5">
                Forecast: Going from 1 week to 1 year might be ~2x easier than going from 1 hour to 1 week. Reasoning: 1 week tasks can be
              </tspan>
            </text>
            <text 
              x="0" 
              y="690"
              fontSize="15" 
              fontFamily="monospace"
              className="text-[15px] font-mono"
              fill="#333333"
            >
                much more complex than 1 hour tasks, but we project there aren't as many extra skills needed to go from 1 week to 1 year.
            </text>
            {/* <text 
              x="0" 
              y="690"
              fontSize="14" 
              fontFamily="monospace"
              className="text-[16px] font-mono"
              fill="#333333"
            >
           In other words, we don't think there are as many extra skills needed to do 1 week vs. 1 day of work.
            </text> */}
          {/* Legend - METR's DATA */}
  <g transform={`translate(${margin.left + 15}, ${margin.top + 10})`}>
    <rect x="0" y="0" width="300" height="310" fill="#F5F5F5" rx="5" opacity="0.8" />
    
    {/* Legend title */}
    <text 
      x="150" 
      y="25" 
      textAnchor="middle" 
      fontSize="20" 
      fontWeight="bold" 
      fontFamily="monospace"
      className="text-[20px] font-mono"
    >
      METR's DATA
    </text>
    {/* Legend items */}
    <polygon 
      points="20,42 12,57 28,57" 
      fill={gptcolor} 
      stroke="white" 
      strokeWidth="1" 
    />
    <text 
      x="40" 
      y="55" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      gpt-3.5-turbo-instruct
    </text>
  
    <rect x="12" y="72" width="16" height="16" fill={gptcolor} stroke="white" strokeWidth="1" />
    <text 
      x="40" 
      y="85" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      GPT-4 0314
    </text>
    <polygon 
    points="20,100 28,110 20,120 12,110" 
    fill={gptcolor}
    stroke="white" 
    strokeWidth="1" 
  />
  <text 
    x="40" 
    y="115" 
    fontSize="16" 
    fontFamily="monospace"
    className="text-[16px] font-mono"
  >
  GPT-4 1106
  </text>
    <polygon 
    points="20,130 25,140 20,150 15,140" 
    fill={gptcolor} 
    stroke="white" 
    strokeWidth="1" 
  />
  <text 
    x="40" 
    y="145" 
    fontSize="16" 
    fontFamily="monospace"
    className="text-[16px] font-mono"
  >
    GPT-4o
  </text>
  
  <g transform="translate(20, 200)">
    <line
      x1={-8}
      y1={0}
      x2={8}
      y2={0}
      stroke={gptcolor}
      strokeWidth={5}
    />
    <line
      x1={0}
      y1={-8}
      x2={0}
      y2={8}
      stroke={gptcolor}
      strokeWidth={5}
    />
  </g>
  <text 
    x="40" 
    y="175" 
    fontSize="16" 
    fontFamily="monospace"
    className="text-[16px] font-mono"
  >
    o1-preview
  </text>
  
  <g transform="translate(20, 170)">
    <line
      x1={-6}
      y1={-6}
      x2={6}
      y2={6}
      stroke={gptcolor}
      strokeWidth={5}
    />
    <line
      x1={-6}
      y1={6}
      x2={6}
      y2={-6}
      stroke={gptcolor}
      strokeWidth={5}
    />
  </g>
  <text 
    x="40" 
    y="205" 
    fontSize="16" 
    fontFamily="monospace"
    className="text-[16px] font-mono"
  >
    o1
  </text>
    
    <polygon 
      points="20,220 12,235 28,235" 
      fill="#832000" 
      stroke="white" 
      strokeWidth="1" 
    />
    <text 
      x="40" 
      y="235" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      Claude 3.5 Sonnet (Old)
    </text>
  
    <rect x="12" y="250" width="16" height="16" fill="#832000" stroke="white" strokeWidth="1" />
    <text 
      x="40" 
      y="265" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      Claude 3.5 Sonnet (New)
    </text>
    
    <polygon 
      points="20,280 28,290 20,300 12,290" 
      fill="#832000" 
      stroke="white" 
      strokeWidth="1" 
    />
    <text 
      x="40" 
      y="295" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      Claude 3.7 Sonnet
    </text>
  </g>
  
  {/* Legend - Our Projection */}
  <g transform={`translate(${margin.left + 330}, ${margin.top + 10})`}>
    <rect x="0" y="0" width="220" height="130" fill="#F5F5F5" rx="5" opacity="0.8" />
    
    {/* Legend title */}
    <text 
      x="110" 
      y="25" 
      textAnchor="middle" 
      fontSize="20" 
      fontWeight="bold" 
      fontFamily="monospace"
      className="text-[20px] font-mono"
    >
      AI 2027
    </text>
    
    {/* Legend items */}
    <circle 
      cx="20" 
      cy="50" 
      r="8" 
      fill={forecastcolor} 
      stroke="white" 
      strokeWidth="1" 
    />
    <text 
      x="40" 
      y="55" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      Agent-0
    </text>
    
    <polygon 
      points="20,70 11,87 29,87" 
      fill={forecastcolor}
      stroke="white" 
      strokeWidth="1" 
    />
    <text 
      x="40" 
      y="85" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      Agent-1
    </text>
    
    <rect 
      x="12" 
      y="100" 
      width="16" 
      height="16" 
      fill={forecastcolor}
      stroke="white" 
      strokeWidth="1" 
    />
    <text 
      x="40" 
      y="115" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      Agent-2
    </text>
  </g>
  
  
  {/* Legend - Our Projection */}
  <g transform={`translate(${margin.left + 330}, ${margin.top + 170})`}>
    <rect x="0" y="0" width="220" height="100" fill="#F5F5F5" rx="5" opacity="0.8" />
    
    {/* Legend title */}
    <text 
      x="110" 
      y="25" 
      textAnchor="middle" 
      fontSize="20" 
      fontWeight="bold" 
      fontFamily="monospace"
      className="text-[20px] font-mono"
    >
      APRIL 16 RELEASE
    </text>
    
    {/* Legend items */}
  
    <g transform="translate(20, 50)">
    <line
      x1={-8}
      y1={0}
      x2={8}
      y2={0}
      stroke={newcolor2}
      strokeWidth={5}
    />
    <line
      x1={0}
      y1={-8}
      x2={0}
      y2={8}
      stroke={newcolor2}
      strokeWidth={5}
    />
  </g>
    <text 
      x="40" 
      y="55" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      o4-mini
    </text>
    
  <g transform="translate(20, 80)">
    <line
      x1={-6}
      y1={-6}
      x2={6}
      y2={6}
      stroke={newcolor}
      strokeWidth={5}
    />
    <line
      x1={-6}
      y1={6}
      x2={6}
      y2={-6}
      stroke={newcolor}
      strokeWidth={5}
    />
  </g>
    <text 
      x="40" 
      y="85" 
      fontSize="16" 
      fontFamily="monospace"
      className="text-[16px] font-mono"
    >
      o3
    </text>
  </g>
          
          {/* Model data points with error bars */}
          {modelData.map((model, i) => (
            <g key={`model-${i}`}>
              {drawModelPoint(model)}
              {/* {drawErrorBar(xScale(model.x), model.y, model.errorBar)} */}
            </g>
          ))}
        </svg>
      </div>
    );
  }